<template>
  <div>
    <div class="zoom-actions">
      <v-btn @click="zoomIn">+</v-btn>
      <v-btn class="ml-2" @click="zoomOut">-</v-btn>
      <v-btn class="ml-2" @click="reset">
        <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
      </v-btn>
    </div>

    <!---JSON code-->
    <div class="json-action">
      <v-btn @click="collapseJsonPreview">
        <v-icon> mdi-code-json </v-icon>
      </v-btn>
    </div>

    <div v-if="displayJsonPreview" class="json-preview">
      <div class="json-preview__header">
        <div v-if="jsonFormatError" class="d-flex align-center mb-3">
          <span class="json-preview__format-error">Invalid JSON format!</span>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn v-on="on" @click="undoJson" class="ml-3">
                <v-icon>mdi-undo</v-icon>
              </v-btn>
            </template>
            <p>Undo to previous valid JSON state</p>
          </v-tooltip>
        </div>
      </div>
      <code-input
        :class="{ 'code-error': jsonFormatError }"
        @input="changeJsonInput"
        :key="jsonInputKey"
        :value="audioFormDataJsonFormat"
        :title="''"></code-input>
    </div>
    <!---JSON code END -->

    <div class="panzoom-parent">
      <div ref="panzoom" class="d-flex justify-center">
        <div v-if="!formTree" class="d-flex justify-center">
          <v-btn :color="'primary'" @click="addStep">Add step</v-btn>
        </div>
        <AudioFormTree v-else :form-tree="formTree"></AudioFormTree>
      </div>
    </div>
  </div>
</template>

<script>
  import AudioFormTree from "@/components/shortcodes/audioform/AudioFormTree.vue";
  import Panzoom from "@panzoom/panzoom";
  import { Tree, constructTreeFromObject } from "@/components/shortcodes/audioform/AudioFormTree.js";
  import { STEP } from "@/components/shortcodes/audioform/constant.js";
  import cloneDeep from "lodash/cloneDeep";
  import CodeInput from "@/components/custom/CodeInput";
  import debounce from "lodash.debounce";

  export default {
    name: "AudioFormDialog",
    components: {
      AudioFormTree,
      CodeInput,
    },
    data() {
      return {
        panzoomInstance: null,
        formTree: null,
        /**
         * When editing JSON input (code-input), user might make the JSON inside of the code-input invalid;
         * jsonFormatError is true if JSON format inside of the code-input is incorrect (not parseable using JSON.parse)
         */
        audioFormDataJsonFormat: "",
        displayJsonPreview: false,
        jsonFormatError: false,
        paramData: null,
        jsonInputKey: 0,
      };
    },
    watch: {
      formTree: {
        handler(newValue) {
          this.updateValue(newValue);
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.panzoomInstance = Panzoom(this.$refs.panzoom, {
        canvas: true,
        /**
         * Ignores all panzoom events on elements with provided class;
         * This is required because sometimes we use mouse click and drag to select things (text in the input for example)
         * This prevents panzoom from moving the view when trying to select text
         */
        excludeClass: "panzoom-ignore",
        /**
         * By default, panzoom will call event.preventDefault() and event.stopPropagation() on every pan event.
         * This prevents colorpicker from being closed on outside click.
         * Override the default handleStartEvent function to solve this.
         */
        handleStartEvent: () => {},
      });

      // Set appropriate height for panzoom wrapper. Set it to window innerheight
      this.$refs.panzoom && (this.$refs.panzoom.style.height = `${window.innerHeight - 24}px`);

      const parent = this.$refs.panzoom.parentElement;

      parent.addEventListener("wheel", event => {
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });

          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      });
    },
    methods: {
      changeJsonInput: debounce(function (value) {
        let parsedJson;

        try {
          this.jsonFormatError = false;
          parsedJson = JSON.parse(value);
        } catch (e) {
          this.jsonFormatError = true;
        }

        if (!this.jsonFormatError) {
          /**
           * Since the data was json encoded, we don't have all the tree methods like addChild, removeNode, etc.
           * We have to convert JS object to tree instance.
           */
          this.formTree = constructTreeFromObject(parsedJson);
        }
      }, 1000),
      addStep() {
        this.formTree = new Tree(cloneDeep(STEP));
      },
      reset() {
        this.panzoomInstance.reset();
      },
      zoomIn() {
        this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        this.panzoomInstance.zoomOut();
      },
      /**
       * Updates the JSON input to previous valid state.
       */
      undoJson() {
        const displayData = this.formTree;
        this.audioFormDataJsonFormat = JSON.stringify(displayData, null, " ");
        this.jsonInputKey++;
      },
      collapseJsonPreview() {
        this.displayJsonPreview = !this.displayJsonPreview;
        if (this.displayJsonPreview) {
          this.audioFormDataJsonFormat = JSON.stringify(this.formTree, null, " ");
          this.jsonInputKey++;
        }
      },
      updateValue(val) {
        // Special encoding is needed for emojis:)
        const data = btoa(unescape(encodeURIComponent(JSON.stringify(val))));

        window.parent.postMessage(
          {
            mceAction: "updateAudioFormData",
            data: data,
          },
          origin,
        );
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (message.data.type === "sendData") {
            const { data } = message.data;
            // Special decoding is needed for emojis:)
            const formTreeData = JSON.parse(decodeURIComponent(escape(window.atob(data))));

            /**
             * Since the data was json encoded, we don't have all the tree methods like addChild, removeNode, etc.
             * We have to convert JS object to tree instance.
             */
            this.formTree = constructTreeFromObject(formTreeData);
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";
  .zoom-actions {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
  }

  .json-action {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 102;
  }

  .json-preview {
    position: absolute;
    top: 0;
    left: 5px;
    right: 5px;
    bottom: 5px;
    z-index: 101;
    background-color: get-color("element-bg-200");
    &__header {
      display: flex;
      background: get-color("element-bg-200");
      padding: 70px 20px 0;
      &-title {
        color: get-color("subtitle");
        @include text-body-1-bold();
      }
    }
    &__format-error {
      color: get-color("error");
      @include text-body-1-bold();
    }
  }
  .json-error {
    background-color: get-color("error");
  }
  .code-error {
    border: 3px dashed get-color("error") !important;
  }
</style>
